import { initializeApp, getApps, FirebaseApp } from 'firebase/app'
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth'

// Check if emulator mode is enabled
const isEmulatorMode = import.meta.env.DEV && import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST

// Firebase configuration from environment variables
// For emulator mode, use dummy values since emulator doesn't validate API keys
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (isEmulatorMode ? 'demo-api-key' : ''),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (isEmulatorMode ? 'demo-project.firebaseapp.com' : ''),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || (isEmulatorMode ? 'demo-project' : ''),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || (isEmulatorMode ? 'demo-project.appspot.com' : ''),
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || (isEmulatorMode ? '123456789' : ''),
  appId: import.meta.env.VITE_FIREBASE_APP_ID || (isEmulatorMode ? '1:123456789:web:abcdef' : ''),
}

// Validate that we have minimum required config (unless in emulator mode)
if (!isEmulatorMode && (!firebaseConfig.apiKey || !firebaseConfig.projectId)) {
  console.warn(
    'Firebase configuration is incomplete. ' +
    'Set VITE_FIREBASE_API_KEY and VITE_FIREBASE_PROJECT_ID environment variables, ' +
    'or enable emulator mode with VITE_FIREBASE_AUTH_EMULATOR_HOST'
  )
}

// Initialize Firebase app (only if not already initialized)
let app: FirebaseApp
if (getApps().length === 0) {
  try {
    app = initializeApp(firebaseConfig)
  } catch (error) {
    console.error('Failed to initialize Firebase:', error)
    throw error
  }
} else {
  app = getApps()[0]
}

// Initialize Auth
export const auth: Auth = getAuth(app)

// Connect to emulator if in development and emulator host is set
if (isEmulatorMode) {
  const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST
  try {
    connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true })
    console.log(`✅ Connected to Firebase Auth emulator at http://${emulatorHost}`)
  } catch (error: any) {
    // connectAuthEmulator can throw if already connected, which is fine
    // It can also throw if auth is already initialized, which we handle gracefully
    const errorMessage = error?.message || String(error)
    if (errorMessage.includes('already') || errorMessage.includes('Cannot call connectAuthEmulator')) {
      // Already connected or can't connect - this is fine
      console.log(`ℹ️  Firebase Auth emulator connection: ${errorMessage}`)
    } else {
      console.warn('Failed to connect to Auth emulator:', error)
    }
  }
}

export default app

